/* eslint-disable arrow-body-style */
import React, { useEffect, useState } from 'react';

import { Box, Image } from '@chakra-ui/react';
import { Button, CheckCircleOutline, Download } from '@mesh.ai/rabbit';
import { useFinchConnect } from '@tryfinch/react-connect';
import { useToast } from 'components/hooks/useToast';

import HrisLogoIcon from 'assets/assetsV1/illustrations/HRISLogoIcon.png';

import { FINCH_CLIENT_ID, FINCH_CLIENT_ID_PROD, TOAST_STATUSES } from 'constants/ProjectConstants';
import { sendPageViewEvent } from 'constants/mixpanelHelper';

import { storePermanentAccountTokenFinch, getFinchHRISAuthDetails } from 'services/plugins';

import { PaperWithShadow } from 'uiToolkitV1/Paper';
import { TextBodyMedium, TextHeader2 } from 'uiToolkitV1/Typography';

import { UninstallSection } from './UninstallSection';
import { GuideSection } from './guideSection';
import { InstallationSection } from './installationSection';

export const PLUGIN_TYPE = { FINCH: 'FINCH', MERGE: 'MERGE' };

function FinchHRISIntegration({ isFinchActivated, setIsFinchActivated }) {
  const toast = useToast();

  const onSuccess = async ({ code }) => {
    try {
      await storePermanentAccountTokenFinch({ public_token: code });
      setIsFinchActivated(true);
    } catch (error) {
      console.error('error storing finch token', error);
      toast({
        title: error.message,
        status: TOAST_STATUSES.ERROR,
        position: 'bottom-left',
      });
    }
  };
  const onError = ({ errorMessage }) => {
    console.error('error fetching finch token', errorMessage);
  };

  const { open } = useFinchConnect({
    clientId: process.env.NODE_ENV === 'production' ? FINCH_CLIENT_ID_PROD : FINCH_CLIENT_ID,
    products: ['company', 'directory', 'individual', 'employment'],
    sandbox: false,
    manual: true,
    onSuccess,
    onError,
  });

  if (isFinchActivated || isFinchActivated === null) {
    return (
      <Box display="flex" gap={1} justifyContent="start" alignItems="center">
        <CheckCircleOutline fontSize="18px" color="#1A5724" />
        <TextBodyMedium bold color="#1A5724">
          Installed
        </TextBodyMedium>
      </Box>
    );
  }

  return (
    <Button iconLeft={Download} onClick={open}>
      Install
    </Button>
  );
}

const Header = ({ isFinchActivated, setIsFinchActivated }) => {
  return (
    <Box display="flex" gap={3} alignItems="center">
      <Box p="20px">
        <Image src={HrisLogoIcon} boxSize="80px" />
      </Box>

      <Box display="flex" flexDir="column">
        <TextHeader2 mb={1} bold>
          HRIS Plugin
        </TextHeader2>
        <TextBodyMedium color="text.g2" mb={3}>
          Manage your HR workflow from Mesh, and watch job satisfaction and engagement go through the roof!
        </TextBodyMedium>
        <FinchHRISIntegration setIsFinchActivated={setIsFinchActivated} isFinchActivated={isFinchActivated} />
      </Box>
    </Box>
  );
};

const HRISPlugin = () => {
  const [isFinchActivated, setIsFinchActivated] = useState(null);

  const checkAuthenticationStatus = () => {
    getFinchHRISAuthDetails()
      .then(() => {
        setIsFinchActivated(true);
      })
      .catch(() => {
        setIsFinchActivated(false);
      });
  };

  useEffect(checkAuthenticationStatus, []);

  useEffect(() => {
    sendPageViewEvent({
      eventProperties: {
        source: 'Company-settings-HRIS',
      },
    });
  }, []);

  return (
    <PaperWithShadow display="flex" p={4} flexDir="column" gap="12px">
      <Header isFinchActivated={isFinchActivated} setIsFinchActivated={setIsFinchActivated} />

      <GuideSection />

      {isFinchActivated === false && <InstallationSection />}

      {isFinchActivated === true && <UninstallSection />}
    </PaperWithShadow>
  );
};

export default HRISPlugin;
